import { useClubSlug } from 'components/Clubs/hooks';
import { useApi } from '@hooks/useApi';
import useCheckPermissions from '@hooks/useCheckPermissions';
import { useCallback, useState } from 'react';

interface ConfirmPayload {
    allocationId: number;
    // omit to act on the whole booking, pass an id to toggle a single booker
    bookerId?: number;
    verified: boolean;
}

/**
 * Admin side of the attendance confirmation: the counter variant of the code a booker receives.
 *
 * Also resolves whether the current user may use it at all — the allocation list and the grid
 * preview are only gated by `allocations.view`, so the action has to check `allocations.approve`
 * itself.
 */
export const useAttendanceConfirmation = () => {
    const api = useApi();
    const clubSlug = useClubSlug();
    const [isLoading, setIsLoading] = useState(false);
    const { hasAccess, isLoading: permissionIsLoading } = useCheckPermissions({
        permission: 'space_manager.allocations.approve'
    });

    // hasAccess defaults to true while the check is still running — without the isLoading guard
    // the button would flash up for users who are not allowed to see it
    const canConfirm = !permissionIsLoading && hasAccess;

    const confirm = useCallback(
        ({ allocationId, bookerId, verified }: ConfirmPayload, onSuccess?: () => void) => {
            setIsLoading(true);

            // not muted on purpose: the backend answers a taken slot with 409 and a booking
            // without the effect with 422, and those messages belong on screen
            return api({
                url: `club/${clubSlug}/space-manager/allocation/${allocationId}/attendance`,
                method: 'POST',
                data: { verified, booker_id: bookerId ?? null },
                onSuccess: () => onSuccess?.(),
                onFinish: () => setIsLoading(false)
            });
        },
        [api, clubSlug]
    );

    return { confirm, isLoading, canConfirm };
};

export default useAttendanceConfirmation;
